import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Database, AlertTriangle, FileWarning, GitBranch, ArrowLeft } from 'lucide-react';
import { observabilityApi } from '../../services/api';
import OverviewTab from './tabs/OverviewTab';
import TablesTab from './tabs/TablesTab';
import AnomaliesTab from './tabs/AnomaliesTab';
import IncidentsTab from './tabs/IncidentsTab';
import LineageTab from './tabs/LineageTab';

type TabKey = 'overview' | 'tables' | 'anomalies' | 'incidents' | 'lineage';

const tabs: Array<{ key: TabKey; label: string; icon: any }> = [
  { key: 'overview', label: 'Overview', icon: Activity },
  { key: 'tables', label: 'Tables', icon: Database },
  { key: 'anomalies', label: 'Anomalies', icon: AlertTriangle },
  { key: 'incidents', label: 'Incidents', icon: FileWarning },
  { key: 'lineage', label: 'Lineage', icon: GitBranch },
];

const ObservabilityPage: React.FC = () => {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdParam ? Number(projectIdParam) : undefined;
  const [active, setActive] = useState<TabKey>('overview');

  const projects = useQuery({
    queryKey: ['obs-projects'],
    queryFn: async () => (await observabilityApi.observedProjects()).data.data,
  });

  const project = (projects.data || []).find((p: any) => p.project_id === projectId);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-2">
        <button
          onClick={() => navigate('/observability')}
          className="text-xs text-cargo-muted hover:text-indigo-600 flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> All data products
        </button>
      </div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-cargo-text flex items-center gap-2">
          <Activity className="w-6 h-6 text-indigo-600" />
          {project ? (project.project_name || `Project #${projectId}`) : 'Observability'}
        </h1>
        <p className="text-sm text-cargo-muted mt-1">
          {project?.group_name && <span className="mr-2">{project.group_name} · </span>}
          Freshness, volume, schema drift, quality, and lineage for this project.
        </p>
      </div>

      <div className="border-b border-cargo-border flex gap-1 mb-6">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active === t.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-cargo-muted hover:text-cargo-text'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {active === 'overview' && <OverviewTab onNavigate={setActive} projectId={projectId} />}
      {active === 'tables' && <TablesTab projectId={projectId} />}
      {active === 'anomalies' && <AnomaliesTab projectId={projectId} />}
      {active === 'incidents' && <IncidentsTab projectId={projectId} />}
      {active === 'lineage' && <LineageTab projectId={projectId} />}
    </div>
  );
};

export default ObservabilityPage;
